import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import Multiselect from "@rio-cloud/rio-uikit/Multiselect";
import Switch from "@rio-cloud/rio-uikit/Switch";
import { Select, Spinner } from "@rio-cloud/rio-uikit";
import { isMobile } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import { Asset, AssetModel, CampaignsDetails } from "../../../../../models/asset";
import { CheckupService } from "../../../../../models/checkup";

import { formatPhoneNumber } from "../../../../../utils/phoneUtils";
import {
  IAssetAndDriverForm,
  useSchedulingFormAsset,
} from "../../../../../hooks/useSchedulingForm";
import useAssetModels from "../../../../../hooks/useAssetModels";
import { useDebouncedCallback } from "../../../../../hooks/useDebounce";
import { extractContractModality, extractContractNumber } from "../../../../../utils/contractUtils";

import SearchAutocomplete from "../../../../../components/form/searchAutocomplete";
import VehicleIcon from "../../../../../components/VehicleIcon";
import CopyButton from "../../../../../components/kanban/components/form/components/CopyButton";
import useDarkMode from "../../../../../hooks/useDarkMode";
import DuplicateSchedulingDialog from "../../../../../components/dialogs/DuplicateSchedulingDialog";

interface IAssetsAndDriversProps {
  setStep: (step: number) => void;
  assets: Asset[];
  form?: IAssetAndDriverForm;
  setForm: (form: IAssetAndDriverForm) => void;
}

const AssetsAndDriversForm = (props: IAssetsAndDriversProps) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `intl-msg:appointment.step1.${id}` });
  
  const {
    loading,
    vehicleLoading,
    vehicleError,
    setVehicleError,
    showDuplicateSchedulingDialog,
    activeAppointment,
    closeDuplicateSchedulingDialog,
    searchAssetTerm,
    setSearchAssetTerm,
    setAssetId,
    assetId,
    chooseAsset,
    disableAssetGroup,
    filteredAssets,
    showComments,
    setShowComments,
    setCheckup,
    aditionalServices,
    setCheckupByGroupId,
    formIsValid,
  } = useSchedulingFormAsset(props.setForm, props.form);

  const { assetModels } = useAssetModels();
  const isDarkMode = useDarkMode();

  const assetContractNumber = props.form?.asset?.contractNumber;
  const assetContractModality = props.form?.asset?.contractModality;

  const contractNumber = assetContractNumber ?? extractContractNumber(props.form?.asset ?? {});
  const contractModality = assetContractModality ?? extractContractModality(props.form?.asset ?? {});

  const contractDisplayValue = contractNumber
    ? contractModality
      ? `${contractNumber} • ${contractModality}`
      : contractNumber
    : "";

  // Callbacks debounced para odômetro e horímetro (3000ms de delay)
  const debouncedOdometerCheckup = useDebouncedCallback(
    async (value: number, asset: any) => {
      if (value && asset) {
        await setCheckup(value, "odometer", asset);
      }
    },
    3000
  );

  const debouncedHourmeterCheckup = useDebouncedCallback(
    async (value: number, asset: any) => {
      if (value && asset) {
        await setCheckup(value, "hourmeter", asset);
      }
    },
    3000
  );

  return (
    <>
      <DuplicateSchedulingDialog
        show={showDuplicateSchedulingDialog}
        appointment={activeAppointment}
        onClose={closeDuplicateSchedulingDialog}
      />
      <div className="flex flex-col min-h-[80vh]">
        <div className="scheduling-page p-10 flex-1 overflow-auto">
          <div className="scheduling-page__header">
            <h2 className="text-size-h2 text-color-black font-semibold text-uppercase">
              1. {t('title')}
            </h2>
            <p className="text-color-status-resting text-size-16 padding-top-10">
              {t('subtitle')}
            </p>
          </div>

        <div className="border rounded padding-15 margin-top-20 scheduling-page__fieldset">
          <h6 className="text-bold text-size-16">{t('vehicleInfo')}</h6>

          {!assetId && (
            <div className="flex justify-between w-full gap-6 mt-5">
              <div className="form-group form-group-lg w-full">
                <label htmlFor="placa">
                  {t('searchVehicle')}<span className="asterisk-color">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('searchVehiclePlaceholder')}
                  value={searchAssetTerm}
                  onChange={(e) => {
                    setSearchAssetTerm(e.target.value);
                    setVehicleError(''); // Limpa erro anterior
                  }}
                />
                
                {/* Mensagem de erro para veículos com agendamento existente */}
                {vehicleError && (
                  <div className="mt-3 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-900 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <div className="text-red-700 dark:text-red-300 font-medium" style={{ fontSize: '16px' }}>
                          <p style={{ whiteSpace: 'pre-line' }}>{vehicleError}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {searchAssetTerm && !assetId && (
                  <div className="absolute bg-white dark:bg-neutral-800 rounded shadow p-2 mt-2 z-10 w-[98%] max-h-80 overflow-y-auto">
                    {filteredAssets(props.assets).map((asset) => (
                      <div
                        key={asset.id}
                        className="p-2 mb-2 cursor-pointer dark:text-neutral-200 dark:hover:bg-neutral-700"
                        onClick={async () => {
                          setVehicleError('');
                          const selected = await chooseAsset(
                            asset.id,
                            asset.chassis,
                            asset.name || asset.model,
                            asset.licensePlate
                          );
                          if (selected) {
                            setAssetId(asset.id);
                          }
                        }}
                      >
                        {asset.name} (chassi: {asset.chassis})
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {props.form?.asset && assetId && (
            <div className="border rounded padding-15 margin-top-20 scheduling-page__fieldset">
              <div className="flex items-center gap-4">
                <VehicleIcon 
                  chassis={props.form?.asset.chassis} 
                  size={80}
                />

                <div className="flex-1 padding-left-10 truncate">
                  <p className="text-color-black text-size-16 font-medium m-0">
                    {props.form?.asset.name}
                  </p>
                  <p className="text-color-status-resting text-size-14 m-0 truncate">
                    {t('chassisLabel')} - {props.form?.asset.chassis}
                  </p>
                  {contractNumber && (
                    <p className="text-color-primary text-size-14 m-0 truncate font-medium">
                      {intl.formatMessage({ id: "intl-msg:controlTower.assetsAndDriversForm.contract" }, { contract: contractDisplayValue })}
                    </p>
                  )}
                </div>

                {(props.form?.asset?.plans?.length ?? 0) > 0 && (
                  <div className="flex gap-6 margin-bottom-20">
                    <span className="px-2 py-1 green-tag">RIO</span>
                    <span className="px-2 py-1 green-tag">{t('inWarranty')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div
            className={`flex justify-between w-full ${
              isMobile() ? "flex-wrap" : "mt-5 gap-6"
            } ${props.form?.asset && assetId ? "mt-5" : ""}`}
          >
            <div
              className={`form-group form-group-lg ${
                isMobile() ? "w-full" : "w-1/3"
              }`}
            >
              <label htmlFor="placa" className="truncate">
                {t('licensePlate')}
              </label>
              <input
                id="placa"
                className="form-control"
                type="text"
                value={props.form?.asset?.licensePlate || ""}
                onChange={(e) =>
                  props.setForm({
                    ...props.form,
                    asset: props.form?.asset
                      ? {
                          ...props.form.asset,
                          licensePlate: e.target.value,
                        }
                      : undefined,
                  })
                }
                disabled={!!props.form?.asset?.licensePlateFromEP}
                maxLength={7}
              />
            </div>
            <div
              className={`form-group form-group-lg ${
                isMobile() ? "w-full" : "w-1/3"
              }`}
            >
              <label htmlFor="contractNumber" className="truncate">
                {t('contractVolksTotal')}
              </label>
              <div className="relative">
                <input
                  id="contractNumber"
                  className="form-control pr-10 font-semibold text-color-primary"
                  type="text"
                  value={contractDisplayValue}
                  disabled
                  placeholder={t('noContract')}
                />
                {contractNumber && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <CopyButton textToCopy={contractNumber} />
                  </div>
                )}
              </div>
            </div>
            <div
              className={`form-group form-group-lg ${
                isMobile() ? "w-full" : "w-1/3"
              }`}
            >
              <label htmlFor="emissionStandard" className="truncate">
                {t('legislation')}
              </label>
              {props.form?.asset?.emissionStandard ? (
                <input
                  id="emissionStandard"
                  className="form-control"
                  type="text"
                  value={props.form?.asset?.emissionStandard}
                  disabled
                />
              ) : (
                <Select
                  className="select"
                  id="emissionStandard"
                  placeholder={t('selectLegislation')}
                  options={[
                    { id: "EURO 3", label: "EURO 3" },
                    { id: "EURO 5", label: "EURO 5" },
                    { id: "EURO 6", label: "EURO 6" },
                    { id: "ELETRICO", label: intl.formatMessage({ id: "intl-msg:controlTower.assetsAndDriversForm.electric" }) },
                  ]}
                  value={props.form?.asset?.emissionStandard ? [props.form?.asset?.emissionStandard] : []}
                  onChange={(option: any) => {
                    props.setForm({
                      ...props.form,
                      asset: props.form?.asset
                        ? { ...props.form.asset, emissionStandard: option?.id }
                        : undefined,
                    });
                  }}
                />
              )}
            </div>

            <div
              className={`form-group form-group-lg ${
                isMobile() ? "w-full" : "w-1/3"
              }`}
            >
              <label htmlFor="model" className="truncate">
                {t('vehicleModel')}*
              </label>
              {
                props.form?.asset?.model && props.form?.asset?.model !== "-" ? (
                  <input
                    id="model"
                    className="form-control"
                    type="text"
                    value={props.form?.asset?.model}
                    disabled
                  />
                ) : (
                  <SearchAutocomplete
                    id="model"
                    placeholder={t('vehicleModel')}
                    fetchOptions={async (searchTerm) => assetModels
                      .filter((model) => model.modelId && model.description && model.description.toLowerCase().includes(searchTerm?.toLowerCase()))
                    }
                    optionLabel={{
                      modelCode: t('code'),
                      description: t('model'),
                    }}
                    optionRenderer={(model: AssetModel) => (
                      <div>
                        <strong>{t('code')}:</strong> {model.modelCode} - <strong>{t('model')}:</strong> {model.description ?? "-"}
                      </div>
                    )}
                    onSelectedOption={(model: AssetModel) => {
                      props.form?.asset &&
                        props.setForm({
                          ...props.form,
                          asset: {
                            ...props.form?.asset,
                            model: model.description,
                            modelCode: model.modelCode,
                            vehicleModel: model.modelId,
                          },
                        });
                    }}
                    overlay
                    overlayClassName="!w-[44%]"
                    defaultValue={props.form?.asset?.model && assetModels.find((model) => model.modelId === props.form?.asset?.model)}
                  />
                )
              }
            </div>

            <div
              className={`form-group form-group-lg ${
                isMobile() ? "w-full" : "w-1/3"
              }`}
            >
              <label htmlFor="group" className="truncate">
                {t('maintenanceGroup')}*
              </label>
              <Select
                className="select"
                id="group"
                placeholder={t('searchMaintenanceGroup')}
                options={[
                  { id: "1", label: t('groupRoad') },
                  { id: "2", label: t('groupMixed') },
                  { id: "3", label: t('groupSevere') },
                  { id: "4", label: t('groupSpecial') },
                ].map((group: any) => ({ id: group.id, label: group.label }))}
                value={
                  props.form?.asset?.group ? [props.form?.asset?.group] : []
                }
                useFilter
                noItemMessage={t('noGroupFound')}
                onChange={async (e) => setCheckupByGroupId(e?.id)}
                disabled={disableAssetGroup || loading}
              />
            </div>
          </div>

          <div className="flex justify-between w-full gap-6">
            <div className="form-group form-group-lg w-1/2">
              <label htmlFor="odometer" className="truncate">
                {t('odometer')}*
              </label>
              <div className="input-group">
                <input
                  id="odometer"
                  className="form-control"
                  type="text"
                  value={props.form?.asset?.odometer}
                  disabled={loading}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    props.setForm({
                      ...props.form,
                      asset: props.form?.asset
                        ? {
                            ...props.form.asset,
                            odometer: value,
                            group: props.form?.asset?.group,
                          }
                        : undefined,
                    });
                    // Executa o cálculo com debounce
                    debouncedOdometerCheckup(value, props.form?.asset);
                  }}
                  onBlur={async (e) => {
                    if (e.target.value) {
                      await setCheckup(
                        Number(e.target.value),
                        "odometer",
                        props.form?.asset
                      );
                    }
                  }}
                />
              </div>
            </div>

            <div className="form-group form-group-lg w-1/2">
              <label htmlFor="hourmeter" className="truncate">
                {t('hourmeter')}*
              </label>
              <div className="input-group">
                <input
                  id="hourmeter"
                  className="form-control no-controls"
                  type="number"
                  value={props.form?.asset?.hourmeter}
                  disabled={loading}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    props.setForm({
                      ...props.form,
                      asset: props.form?.asset
                        ? {
                            ...props.form.asset,
                            hourmeter: value,
                            group: props.form?.asset?.group,
                          }
                        : undefined,
                    });
                    // Executa o cálculo com debounce
                    debouncedHourmeterCheckup(value, props.form?.asset);
                  }}
                  onBlur={async (e) => {
                    if (e.target.value) {
                      await setCheckup(
                        Number(e.target.value),
                        "hourmeter",
                        props.form?.asset
                      );
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-column">
            <div
              className={`flex justify-between w-full ${
                isMobile() ? "flex-wrap" : "gap-6"
              }`}
            >
              <div
                className={`form-group form-group-lg ${
                  isMobile() ? "w-full" : "w-1/2"
                }`}
              >
                <label htmlFor="motorista" className="truncate">
                  {t('driverName')}
                </label>
                <input
                  id="motorista"
                  className="form-control"
                  type="text"
                  placeholder={t('driverName')}
                  value={props.form?.driver?.name ?? ""}
                  onChange={(e) => {
                    props.setForm({
                      ...props.form,
                      driver: props.form?.driver
                        ? {
                            ...props.form.driver,
                            name: e.target.value,
                          }
                        : undefined,
                    });
                  }}
                />
              </div>

              <div
                className={`form-group form-group-lg ${
                  isMobile() ? "w-full" : "w-1/2"
                }`}
              >
                <label htmlFor="cnh" className="truncate">
                  {t('driverLicense')}
                </label>
                <div className="input-group">
                  <input
                    id="cnh"
                    className="form-control"
                    type="text"
                    placeholder={t('driverLicense')}
                    maxLength={12}
                    value={props.form?.driver?.license ?? ""}
                    onChange={(e) => {
                      props.setForm({
                        ...props.form,
                        driver: props.form?.driver
                          ? {
                              ...props.form.driver,
                              license: e.target.value,
                            }
                          : undefined,
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              className={`flex justify-between w-full ${
                isMobile() ? "flex-wrap" : "gap-6"
              }`}
            >
              <div
                className={`form-group form-group-lg ${
                  isMobile() ? "w-full" : "w-1/2"
                }`}
              >
                <label htmlFor="emailMotorista" className="truncate">
                  {t('driverEmail')}
                </label>
                <div className="input-group">
                  <span className="input-group-addon">
                    <span className="rioglyph rioglyph-envelope text-color-black"></span>
                  </span>
                  <input
                    id="emailMotorista"
                    className="form-control"
                    type="text"
                    placeholder={t('driverEmail')}
                    value={props.form?.driver?.email}
                    onChange={(e) => {
                      props.setForm({
                        ...props.form,
                        driver: props.form?.driver
                          ? {
                              ...props.form.driver,
                              email: e.target.value,
                            }
                          : undefined,
                      });
                    }}
                    disabled={props.form?.scheduleByDriver}
                  />
                </div>
              </div>

              <div
                className={`form-group form-group-lg ${
                  isMobile() ? "w-full" : "w-1/2"
                }`}
              >
                <label htmlFor="telefoneMotorista" className="truncate">
                  {t('driverPhone')}
                </label>
                <div className="input-group">
                  <span className="input-group-addon">
                    <span className="rioglyph rioglyph-earphone text-color-black"></span>
                  </span>
                  <input
                    id="telefoneMotorista"
                    className="form-control"
                    type="text"
                    placeholder={t('driverPhone')}
                    value={props.form?.driver?.phone}
                    onChange={(e) => {
                      const formattedPhoneNumber = formatPhoneNumber(
                        e.target.value
                      );
                      props.setForm({
                        ...props.form,
                        driver: props.form?.driver
                          ? {
                              ...props.form.driver,
                              phone: formattedPhoneNumber,
                            }
                          : undefined,
                      });
                    }}
                    disabled={props.form?.scheduleByDriver}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded padding-15 margin-top-15 scheduling-page__fieldset">
          <h6 className="text-bold text-size-16">
            {t('reviewsSection')}
          </h6>

          {props.form?.asset?.model && props.form?.asset?.model !== "-" && props.form?.asset?.group && props.form?.asset?.group !== "-" ? (
            <>
            <div className="info-group">
              <p className="text-color-status-resting text-size-16 padding-top-10 padding-bottom-5">
                {t('availableReviews')}*
              </p>

              <div className="display-flex gap-10">
                {[
                  { id: "preventive", label: t('preventive') },
                  { id: "corrective", label: t('corrective') },
                  { id: "workshopActions", label: t('fieldCampaigns') },
                ].map((option) => {
                  if (option.id === "preventive" && (!props.form?.checkup || props.form?.checkup?.parts?.length === 0))
                    return null;
                  if (
                    option.id === "workshopActions" &&
                    !props.form?.asset?.campaigns?.list?.length
                  )
                    return null;
                  return (
                    <div className="avalilable-revisions border rounded">
                      <Checkbox
                        onChange={() => {
                          let schedulingTypes = props.form?.schedulingTypes;

                          schedulingTypes = schedulingTypes?.includes(option.id) ? schedulingTypes?.filter(type => type !== option.id) : [...(schedulingTypes ?? []), option.id];

                          props.setForm({
                            ...props.form,
                            schedulingTypes,
                          });
                        }}
                        checked={props.form?.schedulingTypes?.includes(option.id)}
                        custom
                        className="padding-y-5 padding-x-10"
                      >
                        <div className="checkbox-text-wrapper display-flex gap-10">
                          <div className="checkbox-text" />
                          <div className="flex-1-1">{option.label}</div>
                        </div>
                      </Checkbox>
                    </div>
                  );
                })}
              </div>
            </div>

            {props.form?.asset?.chassis !== "-" &&
            props.form?.schedulingTypes?.length &&
            (props.form?.schedulingTypes?.includes("preventive") ||
              props.form?.schedulingTypes?.includes("workshopActions")) ? (
              <div
                style={{
                  backgroundColor: isDarkMode ? "#0f2e21" : "#ECFDF3",
                  color: isDarkMode ? "#4ade80" : "#067647",
                  borderColor: isDarkMode ? "#166534" : "#ABEFC6",
                }}
                className="border rounded padding-15 margin-top-20"
              >
                {props.form?.schedulingTypes?.includes("preventive") &&
                  props.form?.checkup && (
                    <div className="margin-bottom-20">
                      <div className="text-bold">
                        {props.form?.checkup?.description}
                      </div>
                      {(props.form?.checkup?.services ?? []).map(
                        (service: CheckupService) => (
                          <div className="padding-left-10 padding-top-10">
                            {service.name}
                          </div>
                        )
                      )}
                      {props.form?.checkup?.parts?.length ? (
                        <>
                          <div className="text-bold padding-top-10">
                            {t('partsList')}
                          </div>
                          <div className="display-grid grid-cols-2 gap-x-20 gap-y-5 padding-top-10 padding-left-10">
                            {(props.form?.checkup?.parts ?? []).map((part) => (
                              <div key={part.code} className="display-flex flex-column gap-5">
                                <div className="display-flex align-items-center justify-content-between gap-10">
                                  <span className="flex-1 min-w-0">{part.qtd}x - {part.description} - {part.code}</span>
                                  <span className={`flex-shrink-0 px-2 py-1 rounded text-size-12 font-medium uppercase whitespace-nowrap text-white ${part.isCovered !== false ? "green-tag" : "bg-red-600"}`} title={part.isCovered === false && part.notCoveredReason ? part.notCoveredReason : undefined}>
                                    {part.isCovered !== false ? t('coveredByPlan') : t('noCoverage')}
                                  </span>
                                </div>
                                {part.isCovered === false && part.notCoveredReason?.trim() && (
                                  <div className="text-size-12 text-color-status-resting padding-left-10">
                                    {part.notCoveredReason}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      ) : null}
                    </div>
                  )}

                {props.form?.schedulingTypes?.includes("workshopActions") && (
                  <>
                    <div className="text-bold">{t('fieldCampaigns')}</div>
                    {(props.form?.asset?.campaigns?.list ?? []).map(
                      (campaign: CampaignsDetails) => (
                        <div className="padding-left-10 padding-top-10">
                          {campaign.name}
                        </div>
                      )
                    )}
                  </>
                )}
              </div>
            ) : null}
          </>) : 
          <div className="text-color-status-resting text-size-16 padding-top-10 padding-bottom-5">
            {t('selectModelAndGroup')}
          </div>}
        </div>

        {props.form?.schedulingTypes?.includes("preventive") ? (
          <div className="border rounded padding-15 margin-top-15 scheduling-page__fieldset">
            <div
              className={`flex justify-between ${
                isMobile() ? "flex-wrap" : ""
              }`}
            >
              <h6 className="text-bold text-size-16">
                {t('serviceHiring')}
              </h6>
              <div
                className={`flex items-center padding-left-10 padding-right-10 total-value ${
                  isMobile() ? "w-full mt-5" : ""
                }`}
              >
                <span className="font--bold text-size-14 font-bold total-value__label">
                  {t('total')}
                </span>
                <span className="font-bold text-size-14 padding-left-5 padding-right-5 margin-left-10 total-value__value">
                  {(props.form?.aditionalServices ?? [])
                    .reduce((acc, service) => acc + service.price, 0)
                    .toLocaleString(intl.locale, {
                      style: "currency",
                      currency: "BRL",
                    })}
                </span>
              </div>
            </div>

            <div className="info-group">
              <p className="text-color-status-resting text-size-16 padding-top-10 padding-bottom-5">
                {t('addOtherServices')}
              </p>

              <div className="add-services">
                <Multiselect
                  placeholder={t('selectServices')}
                  options={aditionalServices.map((service) => ({
                    id: service.id,
                    label: service.name,
                    price: service.price,
                  }))}
                  className="select-icon"
                  onChange={(response: any) => {
                    props.setForm({
                      ...props.form,
                      aditionalServices: aditionalServices.filter((service) =>
                        response.includes(service.id)
                      ),
                    });
                  }}
                  value={props.form?.aditionalServices?.map(
                    (service) => service.id
                  )}
                  noItemMessage={t('noAdditionalService')}
                />
                {!isMobile() && (
                  <div className="services-cost">
                    <span className="font-bold text-size-14 padding-left-5 padding-right-5 margin-left-10 total-value__value">
                      {(props.form?.aditionalServices ?? [])
                        .reduce((acc, service) => acc + service.price, 0)
                        .toLocaleString(intl.locale, {
                          style: "currency",
                          currency: "BRL",
                        })}
                    </span>
                  </div>
                )}
                <div className="show-comments margin-top-15 flex justify-end show-comments">
                  <Switch
                    onChange={() => {
                      setShowComments(!showComments);
                      props.setForm({
                        ...props.form,
                        comments: null,
                      });
                    }}
                    checked={showComments || !!props.form?.comments}
                    labelPosition="right"
                    color="secondary"
                  >
                    <span className="text-size-14 text-color-status-resting">
                      {t('comment')}
                    </span>
                  </Switch>
                </div>
                {(showComments || !!props.form?.comments) && (
                  <div className="text-comments margin-top-20">
                    <textarea
                      className="form-control"
                      placeholder={t('commentPlaceholder')}
                      value={props.form?.comments ?? undefined}
                      onChange={(e) => {
                        props.setForm({
                          ...props.form,
                          comments: e.target.value,
                        });
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        <div className="border rounded padding-15 margin-top-15">
          <div className="flex justify-between">
            <h6 className="text-bold text-size-16">{t('generalObservations')}</h6>
          </div>

          <div className="info-group">
            <div className="add-services">
              <div className="text-comments margin-top-10">
                <textarea
                  className="form-control"
                  placeholder={t('vehicleIssuesPlaceholder')}
                  value={props.form?.obs}
                  onChange={(e) => {
                    props.setForm({
                      ...props.form,
                      obs: e.target.value,
                    });
                  }}
                ></textarea>
              </div>
              {props.form?.schedulingTypes?.includes("corrective") && (props.form?.obs?.trim() ?? "").length > 0 && (
                <div className="margin-top-15 padding-15 rounded bg-green-100 dark:bg-green-950 border border-green-200 dark:border-green-900 text-color-darker text-size-14">
                  {t('coverageCheck')}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>
      <div className="mt-auto">
        <div className="page-info flex justify-between padding-15">
          <span className="text-color-status-resting font-size-14">
            {t('stepLabel')} <strong className="text-color-danger">1</strong> {t('ofLabel')}{" "}
            {props.form?.schedulingTypes?.includes("assistance") ? 3 : 4}
          </span>
          <span className="text-color-status-resting font-size-14">
            {t('stepInfo')}
          </span>
        </div>
        <div className="flex justify-between navigation-buttons step-1">
          <button
            className="px-4 py-2 rounded-lg font-semibold button-color"
            onClick={() => navigate("/control-tower/monitoring")}
          >
            <span className="rioglyph rioglyph-arrow-left text-size-16"></span>{" "}
            {t('back')}
          </button>
          <button
            disabled={!formIsValid()}
            onClick={() => props.setStep(2)}
            className="px-4 py-2 btn btn-primary"
          >
            {t('next')}
          </button>
        </div>
        {loading || vehicleLoading && (
          <div className="w-full flex justify-center items-center py-4">
            <Spinner show={loading || vehicleLoading} text={loading ? t('loading') : vehicleLoading ? t('loadingVehicle') : ''} />
          </div>
        )}
      </div>
    </>
  );
};

export default AssetsAndDriversForm;
